import {
  type MeResponse,
  SECOND_FACTOR_METHOD_TITLES,
  type SecondFactorChallenge,
  type SecondFactorLoginResult,
  type SecondFactorMethod,
  type SecondFactorState,
  type SessionView,
  type TotpSetup,
  TRUSTED_DEVICE_DAYS,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { api } from '../lib/api';
import { relative } from '../lib/format';
import { Button } from '../ui/button';
import { Badge, ErrorBox, Spinner, toast } from '../ui/feedback';
import { Checkbox, Field, Input } from '../ui/form';
import { Panel } from '../ui/layout';

/** QR-код подключения: картинкой (data:), без вставки разметки в страницу */
function QrImage({ svg }: { svg: string }) {
  return (
    <img
      src={`data:image/svg+xml;utf8,${encodeURIComponent(svg)}`}
      alt="QR-код для приложения-генератора кодов"
      className="size-48 rounded-md border border-line bg-white p-2"
    />
  );
}

/** Секрет строкой — для ввода вручную, группами по 4 знака */
function SecretText({ secret }: { secret: string }) {
  return (
    <code className="block break-all rounded-md bg-surface-2 px-3 py-2 font-mono text-[14px] tracking-wider text-ink">
      {secret.replace(/(.{4})/g, '$1 ').trim()}
    </code>
  );
}

/** Коды восстановления: показываются один раз */
export function RecoveryCodesView({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  return (
    <div className="flex flex-col gap-4">
      <p className="text-[14px] text-ink-2">
        Сохраните эти коды — например, распечатайте или запишите в менеджер паролей. Каждый код входит один
        раз, если телефона с приложением нет под рукой. Больше портал их не покажет.
      </p>
      <ul className="grid grid-cols-2 gap-2 font-mono text-[15px] text-ink">
        {codes.map((c) => (
          <li key={c} className="rounded-md bg-surface-2 px-3 py-2 text-center">
            {c}
          </li>
        ))}
      </ul>
      <Button
        variant="secondary"
        onClick={() => {
          navigator.clipboard?.writeText(codes.join('\n')).then(
            () => toast('Коды скопированы'),
            () => {},
          );
        }}
      >
        Скопировать коды
      </Button>
      <Button variant="primary" size="lg" onClick={onDone}>
        Коды сохранены — продолжить
      </Button>
    </div>
  );
}

/**
 * Шаг кода при входе (ДС-12): после верного пароля. Если второй фактор обязателен, но не подключён, — сначала
 * подключить приложение-генератор (QR-код), затем ввести из него код.
 */
export function SecondFactorStep({
  challenge,
  onDone,
  onCancel,
}: {
  challenge: SecondFactorChallenge['secondFactor'];
  onDone: (me: MeResponse) => void;
  onCancel: () => void;
}) {
  const [method, setMethod] = useState<SecondFactorMethod>(challenge.methods[0] ?? 'totp');
  const [code, setCode] = useState('');
  const [trust, setTrust] = useState(false);
  const [setup, setSetup] = useState<TotpSetup | null>(null);
  const [codes, setCodes] = useState<{ codes: string[]; me: MeResponse } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function startEnroll() {
    setBusy(true);
    setError(null);
    try {
      setSetup(
        await api<TotpSetup>('/auth/login/second-factor/enroll', { body: { ticket: challenge.ticket } }),
      );
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function sendTelegram() {
    setError(null);
    try {
      await api('/auth/login/second-factor/telegram', { body: { ticket: challenge.ticket } });
      toast('Код отправлен в Telegram');
    } catch (err) {
      setError(err);
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api<SecondFactorLoginResult>('/auth/login/second-factor', {
        body: { ticket: challenge.ticket, method, code, trustDevice: trust },
      });
      const { recoveryCodes, ...me } = r;
      if (recoveryCodes?.length) setCodes({ codes: recoveryCodes, me });
      else onDone(me);
    } catch (err) {
      setError(err);
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  if (codes) return <RecoveryCodesView codes={codes.codes} onDone={() => onDone(codes.me)} />;

  if (challenge.enroll && !setup)
    return (
      <div className="flex flex-col gap-4">
        <p className="text-[14px] text-ink-2">
          Для вашей роли вход защищён вторым фактором: кроме пароля нужен код из приложения на телефоне —
          Google Authenticator, Яндекс Ключ, Microsoft Authenticator или любое другое приложение-генератор
          кодов. Подключение занимает минуту и делается один раз.
        </p>
        <ErrorBox error={error} />
        <Button variant="primary" size="lg" loading={busy} onClick={startEnroll}>
          Подключить приложение
        </Button>
        <Button variant="secondary" onClick={onCancel}>
          Назад
        </Button>
      </div>
    );

  return (
    <form onSubmit={submit} className="flex flex-col gap-5" noValidate>
      {setup ? (
        <div className="flex flex-col gap-3">
          <p className="text-[14px] text-ink-2">
            1. Откройте приложение-генератор кодов и отсканируйте QR-код. Если сканировать нечем — добавьте
            ключ вручную.
          </p>
          <QrImage svg={setup.qrSvg} />
          <SecretText secret={setup.secret} />
          <p className="text-[14px] text-ink-2">2. Введите шестизначный код, который показало приложение.</p>
        </div>
      ) : challenge.methods.length > 1 ? (
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-[14px] text-ink-2">Чем подтвердить вход</legend>
          {challenge.methods.map((m) => (
            <label
              key={m}
              className="inline-flex min-h-11 cursor-pointer items-center gap-2 text-[15px] text-ink"
            >
              <input
                type="radio"
                name="method"
                value={m}
                checked={method === m}
                onChange={() => {
                  setMethod(m);
                  setCode('');
                }}
                className="size-4 accent-[var(--color-ink)]"
              />
              {SECOND_FACTOR_METHOD_TITLES[m]}
            </label>
          ))}
        </fieldset>
      ) : null}
      {!setup && method === 'telegram' ? (
        <button
          type="button"
          onClick={sendTelegram}
          className="inline-flex min-h-11 items-center self-start text-[14px] font-medium text-ink underline underline-offset-4"
        >
          Прислать код в Telegram ещё раз
        </button>
      ) : null}
      <Field
        label={method === 'recovery' ? 'Код восстановления' : 'Код'}
        htmlFor="code"
        hint={
          method === 'recovery'
            ? 'Вида abcd-efgh, из списка, сохранённого при подключении'
            : method === 'telegram'
              ? 'Шесть цифр из сообщения бота портала'
              : 'Шесть цифр из приложения-генератора'
        }
      >
        <Input
          id="code"
          autoComplete="one-time-code"
          inputMode={method === 'recovery' ? 'text' : 'numeric'}
          maxLength={method === 'recovery' ? 9 : 6}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          autoFocus
        />
      </Field>
      <Checkbox
        label={`Доверять этому устройству ${TRUSTED_DEVICE_DAYS} дней`}
        description="Код не будет спрашиваться на этом браузере. Не отмечайте на чужом компьютере."
        checked={trust}
        onChange={(e) => setTrust(e.target.checked)}
      />
      <ErrorBox error={error} />
      <Button type="submit" variant="primary" size="lg" loading={busy} disabled={code.trim().length < 6}>
        Войти
      </Button>
      <Button type="button" variant="secondary" onClick={onCancel}>
        Назад
      </Button>
    </form>
  );
}

const stateQuery = {
  queryKey: ['second-factor'],
  queryFn: () => api<SecondFactorState>('/auth/second-factor'),
};

/** «Профиль → Второй фактор» (ДС-12): подключить приложение, новые коды восстановления, забыть устройства */
export function SecondFactorPanel() {
  const qc = useQueryClient();
  const state = useQuery(stateQuery);
  const [setup, setSetup] = useState<TotpSetup | null>(null);
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [mode, setMode] = useState<'idle' | 'recovery' | 'disable' | 'replace'>('idle');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<unknown>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: stateQuery.queryKey });

  const run = useMutation({
    mutationFn: async (fn: () => Promise<void>) => fn(),
    onMutate: () => setError(null),
    onError: (err) => setError(err),
  });

  if (state.isLoading)
    return (
      <Panel title="Второй фактор">
        <Spinner />
      </Panel>
    );
  const s = state.data;
  if (!s)
    return (
      <Panel title="Второй фактор">
        <ErrorBox error={state.error} />
      </Panel>
    );

  if (codes)
    return (
      <Panel title="Коды восстановления">
        <RecoveryCodesView
          codes={codes}
          onDone={() => {
            setCodes(null);
            refresh();
          }}
        />
      </Panel>
    );

  return (
    <Panel title="Второй фактор">
      <div className="flex flex-col gap-4">
        <p className="max-w-prose text-[14px] text-ink-2">
          Кроме пароля при входе спрашивается код из приложения на телефоне или из Telegram.{' '}
          {s.required
            ? 'Для вашей роли (деньги, администрирование или внешний участник) он обязателен.'
            : 'Для вашей роли он не обязателен, но защищает вход, если пароль узнают.'}{' '}
          Вход по Face ID, Touch ID и Windows Hello код не спрашивает.
        </p>
        <div className="flex flex-wrap gap-2">
          <Badge tone={s.totp ? 'success' : 'neutral'}>Приложение: {s.totp ? 'подключено' : 'нет'}</Badge>
          <Badge tone={s.telegram ? 'success' : 'neutral'}>Telegram: {s.telegram ? 'привязан' : 'нет'}</Badge>
          {s.totp ? (
            <Badge tone={s.recoveryLeft > 2 ? 'neutral' : 'warning'}>
              Кодов восстановления: {s.recoveryLeft}
            </Badge>
          ) : null}
          {s.trustedDevices ? <Badge tone="neutral">Доверенных устройств: {s.trustedDevices}</Badge> : null}
        </div>
        <ErrorBox error={error} />
        {setup ? (
          <form
            className="flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              run.mutate(async () => {
                const r = await api<{ recoveryCodes: string[] }>('/auth/second-factor/totp/confirm', {
                  body: { code },
                });
                setSetup(null);
                setCode('');
                setCodes(r.recoveryCodes);
                toast('Приложение подключено');
              });
            }}
          >
            <p className="text-[14px] text-ink-2">
              Отсканируйте QR-код в приложении-генераторе или введите ключ вручную.
            </p>
            <QrImage svg={setup.qrSvg} />
            <SecretText secret={setup.secret} />
            <Field label="Код из приложения" htmlFor="sf-code">
              <Input
                id="sf-code"
                autoComplete="one-time-code"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </Field>
            <Button type="submit" variant="primary" loading={run.isPending} disabled={code.length < 6}>
              Подтвердить
            </Button>
            <Button type="button" variant="secondary" onClick={() => setSetup(null)}>
              Отмена
            </Button>
          </form>
        ) : mode === 'recovery' ? (
          <form
            className="flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              run.mutate(async () => {
                const r = await api<{ recoveryCodes: string[] }>('/auth/second-factor/recovery', {
                  body: { code },
                });
                setMode('idle');
                setCode('');
                setCodes(r.recoveryCodes);
              });
            }}
          >
            <Field
              label="Код из приложения"
              htmlFor="sf-rec"
              hint="Прежние коды восстановления перестанут действовать"
            >
              <Input
                id="sf-rec"
                autoComplete="one-time-code"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </Field>
            <Button type="submit" variant="primary" loading={run.isPending} disabled={code.length < 6}>
              Выдать новые коды
            </Button>
            <Button type="button" variant="secondary" onClick={() => setMode('idle')}>
              Отмена
            </Button>
          </form>
        ) : mode === 'replace' ? (
          <form
            className="flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              run.mutate(async () => {
                setSetup(await api<TotpSetup>('/auth/second-factor/totp/start', { body: { password } }));
                setMode('idle');
                setPassword('');
              });
            }}
          >
            <Field
              label="Пароль"
              htmlFor="sf-pw-new"
              hint="Прежний телефон перестанет давать коды после подтверждения"
            >
              <Input
                id="sf-pw-new"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
            <Button type="submit" variant="primary" loading={run.isPending} disabled={!password}>
              Продолжить
            </Button>
            <Button type="button" variant="secondary" onClick={() => setMode('idle')}>
              Отмена
            </Button>
          </form>
        ) : mode === 'disable' ? (
          <form
            className="flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              run.mutate(async () => {
                await api('/auth/second-factor/totp/disable', { body: { password } });
                setMode('idle');
                setPassword('');
                toast('Приложение отключено');
                await refresh();
              });
            }}
          >
            <Field label="Пароль" htmlFor="sf-pw">
              <Input
                id="sf-pw"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
            <Button type="submit" variant="danger" loading={run.isPending} disabled={!password}>
              Отключить приложение
            </Button>
            <Button type="button" variant="secondary" onClick={() => setMode('idle')}>
              Отмена
            </Button>
          </form>
        ) : (
          <div className="flex flex-wrap gap-2">
            {s.totpAvailable ? (
              <Button
                variant={s.totp ? 'secondary' : 'primary'}
                loading={run.isPending}
                onClick={() =>
                  s.totp
                    ? setMode('replace')
                    : run.mutate(async () => {
                        setSetup(await api<TotpSetup>('/auth/second-factor/totp/start', { body: {} }));
                      })
                }
              >
                {s.totp ? 'Подключить другой телефон' : 'Подключить приложение'}
              </Button>
            ) : (
              <p className="text-[14px] text-ink-2">
                Подключить приложение пока нельзя: администратор портала не задал ключ шифрования на сервере.
              </p>
            )}
            {s.totp ? (
              <Button variant="secondary" onClick={() => setMode('recovery')}>
                Новые коды восстановления
              </Button>
            ) : null}
            {s.trustedDevices ? (
              <Button
                variant="secondary"
                onClick={() =>
                  run.mutate(async () => {
                    await api('/auth/second-factor/forget-devices', { body: {} });
                    toast('Доверенные устройства забыты — код спросят снова');
                    await refresh();
                  })
                }
              >
                Забыть доверенные устройства
              </Button>
            ) : null}
            {s.totp && (!s.required || s.telegram) ? (
              <Button variant="secondary" onClick={() => setMode('disable')}>
                Отключить приложение
              </Button>
            ) : null}
          </div>
        )}
        {!s.telegram ? (
          <p className="text-[13px] text-ink-2">
            Чтобы получать код в Telegram, привяжите его в разделе «Уведомления» выше.
          </p>
        ) : null}
      </div>
    </Panel>
  );
}

const sessionsQuery = { queryKey: ['sessions'], queryFn: () => api<SessionView[]>('/auth/sessions') };

/** «Сеансы входа» (НФ-11): где открыт вход, закрыть один или все, кроме этого */
export function SessionsPanel() {
  const qc = useQueryClient();
  const list = useQuery(sessionsQuery);
  const [error, setError] = useState<unknown>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: sessionsQuery.queryKey });
  const end = useMutation({
    mutationFn: (id: string) => api(`/auth/sessions/${id}/revoke`, { body: {} }),
    onMutate: () => setError(null),
    onSuccess: async () => {
      toast('Сеанс завершён');
      await refresh();
    },
    onError: setError,
  });
  const others = useMutation({
    mutationFn: () => api<{ ok: true; ended: number }>('/auth/logout-others', { body: {} }),
    onMutate: () => setError(null),
    onSuccess: async (r) => {
      toast(r.ended ? `Завершено сеансов на других устройствах: ${r.ended}` : 'Других сеансов не было');
      await refresh();
    },
    onError: setError,
  });
  const rows = list.data ?? [];
  return (
    <Panel title="Сеансы входа">
      <p className="mb-4 max-w-prose text-[14px] text-ink-2">
        Где сейчас открыт вход. Если вход остался на чужом компьютере или потерян телефон — завершите сеанс.
        На том устройстве понадобится войти заново, доверенные устройства тоже забываются.
      </p>
      <ErrorBox error={error ?? list.error} className="mb-3" />
      {list.isLoading ? (
        <Spinner />
      ) : (
        <ul className="mb-4 flex flex-col divide-y divide-line">
          {rows.map((s) => (
            <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <div className="min-w-0">
                <div className="text-[14px] font-medium text-ink">
                  {s.device}
                  {s.current ? (
                    <span className="ml-2 text-[12px] font-normal text-ink-2">это устройство</span>
                  ) : null}
                </div>
                <div className="text-[12px] text-ink-2">
                  {s.ip ?? 'адрес неизвестен'} · активен {relative(s.lastSeenAt)}
                </div>
              </div>
              {s.current ? null : (
                <Button
                  variant="secondary"
                  loading={end.isPending && end.variables === s.id}
                  onClick={() => end.mutate(s.id)}
                >
                  Завершить
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      <Button loading={others.isPending} onClick={() => others.mutate()} disabled={rows.length < 2}>
        Выйти на других устройствах
      </Button>
    </Panel>
  );
}
